import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { FormattedMessage } from '@flipster/i18n';
import { Tooltip } from '@flipster/react/design-system/tooltip';
import { Explain } from '@flipster/react/design-utils/explain';
import Formatted from '@flipster/react/design-utils/formatted';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { css } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { UsdtCurrency } from '@flipster/utils/constants/currency';
import Num from '@flipster/utils/num';

import { NumberInputWithSuffix } from '../../../../../input/number-input-with-suffix';
import { copyTradingSettingsBunja } from '../../state';

export const CopyMultiplierSection = () => {
  const { copyExecutionModeAtom, copyMultiplierTextAtom, setCopyMultiplierText, copyMultiplierErrorMessageAtom } =
    useBunja(copyTradingSettingsBunja);

  const copyExecutionMode = useAtomValue(copyExecutionModeAtom);
  const copyMultiplierText = useAtomValue(copyMultiplierTextAtom);
  const copyMultiplierErrorMessage = useAtomValue(copyMultiplierErrorMessageAtom);

  if (copyExecutionMode !== 'FIXED_MULTIPLIER') return null;

  const hasError = Boolean(copyMultiplierErrorMessage);
  const multiplierValue = Num(copyMultiplierText || 0);
  const exampleMasterAmount = 100;
  const exampleYourAmount = multiplierValue.multipliedBy(exampleMasterAmount);

  return (
    <div className={stack({ gap: 8 })}>
      <div className={hstack({ gap: 4 })}>
        <p className={css({ textStyle: 'Text/M/Strong', color: 'content.default.level-5' })}>
          <FormattedMessage id='Copy.r260205.Copy_multiplier' />
        </p>
        <Tooltip.Root placement='right'>
          <Tooltip.Trigger>
            <InformationCircleIcon size={16} color={token('colors.content.default.level-1')} />
          </Tooltip.Trigger>
          <Tooltip.Content bottomSheetTitle={<FormattedMessage id='Copy.r260205.Copy_multiplier' />}>
            <FormattedMessage id='Copy.r260205.Opens_each_copied_position_at_a_fixed_multiple_of_the_master_s_position_size' />
            {'\n'}
            <FormattedMessage id='Copy.r260205.High_multiplier_may_cause_frequent_failed_orders_due_to_insufficient_margin' />
          </Tooltip.Content>
        </Tooltip.Root>
      </div>
      <div
        className={css({
          gap: 16,
          p: 16,
          borderColor: hasError ? 'border.state.negative' : 'border.default.level-2',
          borderWidth: '1px',
          borderStyle: 'solid',
          rounded: 8,
          textStyle: 'Text/M/Regular',
          transition: 'border-color 200ms',
          _focusWithin: {
            borderColor: hasError ? 'border.state.negative' : 'border.default.level-4',
          },
        })}
      >
        <NumberInputWithSuffix
          value={copyMultiplierText}
          onChange={setCopyMultiplierText}
          precision={2}
          suffix={<p className={css({ color: 'content.default.level-5' })}>{copyMultiplierText ? 'x' : <wbr />}</p>}
        />
      </div>
      <Explain.Error>{copyMultiplierErrorMessage}</Explain.Error>
      {!hasError && (
        <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3', whiteSpace: 'pre' })}>
          <FormattedMessage
            id='Copy.r260205.If_master_opens_amount_symbol_position'
            values={{
              amountSymbol1: (
                <Formatted.Number
                  key='symbol'
                  value={exampleMasterAmount}
                  suffix={UsdtCurrency}
                  css={{ textStyle: 'Text/M/Monospace/Strong' }}
                />
              ),
            }}
          />
          {'\n'}
          <FormattedMessage
            id='Copy.r260205.Your_position_size_will_be_amount_symbol'
            values={{
              amountSymbol2: copyMultiplierText ? (
                <Formatted.Number
                  key='symbol'
                  value={exampleYourAmount}
                  suffix={UsdtCurrency}
                  css={{ textStyle: 'Text/M/Monospace/Strong' }}
                />
              ) : (
                '-'
              ),
            }}
          />
        </p>
      )}
    </div>
  );
};
